import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { Banner } from '../ui/Banner';
import { formatDate, localeTag } from '../../utils/format';
import { pokazUwagi, tonUwag, pokazLogowanie, tonLogowania } from '../../utils/ksefUwaga';

// F.19: faktury, które nie trafiły do KSeF, i kończący się certyfikat albo token. Dane z
// GET /api/ksef-attention: faktury widzi każdy zalogowany, `auth` dostaje tylko administrator.

/**
 * Stan uwag KSeF firmy. Odpowiedź inna niż 200 (stary backend bez trasy, wylogowanie) i błąd sieci
 * dają ciszę: baner jest dodatkiem i nie może zepsuć strony. Starsza odpowiedź nie nadpisuje nowszej.
 */
export function useKsefUwagi() {
  const [dane, setDane] = useState(null);
  const numer = useRef(0);
  const odswiez = useCallback(async () => {
    const nr = ++numer.current;
    try {
      const r = await fetch(`${API_BASE}/api/ksef-attention`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      if (nr === numer.current) setDane(d);
    } catch (_) { /* cisza */ }
  }, []);
  useEffect(() => { odswiez(); }, [odswiez]);
  return { dane, odswiez };
}

const Odstep = styled.div`
  display: grid;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
`;
const Zdanie = styled.p`
  margin: 0 0 var(--sp-1);
  font-size: var(--fs-sm);
  color: var(--text);
`;
const Szczegoly = styled.details`
  margin-top: var(--sp-2);
  summary {
    cursor: pointer;
    font-size: var(--fs-sm);
    font-weight: 600;
  }
`;
const Lista = styled.ul`
  list-style: none;
  margin: var(--sp-2) 0 0;
  padding: 0;
  display: grid;
  gap: var(--sp-2);
`;
const Pozycja = styled.li`
  font-size: var(--fs-sm);
  color: var(--text);
  line-height: 1.45;
  overflow-wrap: anywhere;
  a { font-weight: 600; color: inherit; }
`;
const Przyczyna = styled.span`
  display: block;
  color: var(--text-muted);
`;

function useTytulUwag() {
  const { t, currentLanguage } = useTranslation();
  return (n) => {
    switch (new Intl.PluralRules(localeTag(currentLanguage)).select(n)) {
      case 'one': return t('ksefUwaga.titleOne', { count: n });
      case 'few': return t('ksefUwaga.titleFew', { count: n });
      case 'many': return t('ksefUwaga.titleMany', { count: n });
      default: return t('ksefUwaga.titleOther', { count: n });
    }
  };
}

function PozycjaUwagi({ p }) {
  const { t, currentLanguage } = useTranslation();
  const data = (ymd) => formatDate(ymd, { locale: currentLanguage });
  let stan;
  if (p.duplikat) stan = t('ksefUwaga.state.duplicate');
  else if (p.stan === 'blad') stan = t('ksefUwaga.state.error');
  else if (p.stan === 'odrzucona') stan = t('ksefUwaga.state.rejected');
  else if (p.stan === 'czeka') stan = t('ksefUwaga.state.waiting');
  else stan = t('ksefUwaga.state.notSent');
  let termin = null;
  if (p.terminDzis) termin = t('ksefUwaga.deadlineToday');
  else if (p.poTerminie) termin = t('ksefUwaga.deadlinePassed', { date: data(p.termin) });
  else if (p.termin) termin = t('ksefUwaga.deadline', { date: data(p.termin) });
  let podpowiedz = null;
  if (p.duplikat) podpowiedz = t('ksefUwaga.duplicateHint');
  else if (p.recznie) podpowiedz = t('ksefUwaga.simplifiedHint');
  else if (p.stan === 'czeka') podpowiedz = t('ksefUwaga.waitingHint');
  return (
    <Pozycja data-cy="ksef-attention-item" data-invoice-id={p.id}>
      <Link to={`/invoices/${p.id}`}>{p.numer}</Link>
      {' · '}{stan}
      {' · '}{t('ksefUwaga.issued', { date: data(p.dataWystawienia) })}
      {termin ? <>{' · '}<strong>{termin}</strong></> : null}
      {p.przyczyna && !p.duplikat ? <Przyczyna data-cy="ksef-attention-reason">{p.przyczyna}</Przyczyna> : null}
      {podpowiedz ? <Przyczyna>{podpowiedz}</Przyczyna> : null}
    </Pozycja>
  );
}

/** Baner o fakturach poza KSeF z listą najstarszych pozycji i przyczyną zwykłym tekstem. */
export function KsefUwagaBaner({ dane, onPokaz }) {
  const { t } = useTranslation();
  const tytul = useTytulUwag();
  if (!pokazUwagi(dane)) return null;
  const pozycje = Array.isArray(dane.pozycje) ? dane.pozycje : [];
  return (
    <Banner
      tone={tonUwag(dane)}
      title={tytul(dane.liczba)}
      data-cy="ksef-attention-banner"
      action={onPokaz ? { label: t('ksefUwaga.show'), onClick: onPokaz, dataCy: 'ksef-attention-show' } : undefined}
    >
      <Zdanie>{t('ksefUwaga.lead')}</Zdanie>
      {dane.poTerminie > 0 && <Zdanie data-cy="ksef-attention-overdue">{t('ksefUwaga.overdue', { count: dane.poTerminie })}</Zdanie>}
      {dane.terminDzis > 0 && <Zdanie data-cy="ksef-attention-today">{t('ksefUwaga.dueToday', { count: dane.terminDzis })}</Zdanie>}
      {pozycje.length > 0 && (
        <Szczegoly>
          <summary>{t('ksefUwaga.details')}</summary>
          <Lista>{pozycje.map((p) => <PozycjaUwagi key={p.id} p={p} />)}</Lista>
          {dane.liczba > pozycje.length && (
            <Zdanie style={{ marginTop: 'var(--sp-2)' }}>{t('ksefUwaga.more', { shown: pozycje.length, count: dane.liczba })}</Zdanie>
          )}
        </Szczegoly>
      )}
    </Banner>
  );
}

/** Baner administratora o certyfikacie albo tokenie KSeF (od 30 dni przed końcem). */
export function KsefLogowanieBaner({ auth, bezPrzycisku = false }) {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  if (!pokazLogowanie(auth)) return null;
  const data = (ymd) => formatDate(ymd, { locale: currentLanguage });
  let tresc;
  if (auth.metoda === 'certificate') {
    if (auth.poziom === 'wygasl') tresc = t('ksefUwaga.certExpired', { date: data(auth.certDo) });
    else if (auth.certDni === 0) tresc = t('ksefUwaga.certToday');
    else if (auth.certDni === 1) tresc = t('ksefUwaga.certTomorrow', { date: data(auth.certDo) });
    else tresc = t('ksefUwaga.certSoon', { date: data(auth.certDo), count: auth.certDni });
  } else {
    tresc = auth.poziom === 'wygasl'
      ? t('ksefUwaga.tokenPassed', { date: data(auth.tokenDo) })
      : t('ksefUwaga.tokenSoon', { date: data(auth.tokenDo) });
  }
  return (
    <Banner
      tone={tonLogowania(auth)}
      data-cy="ksef-auth-banner"
      action={bezPrzycisku ? undefined : { label: t('ksefUwaga.openSettings'), onClick: () => navigate('/ksef-settings'), dataCy: 'ksef-auth-settings' }}
    >
      {tresc}
    </Banner>
  );
}

/** Oba banery razem, z odstępem pod spodem tylko wtedy, gdy któryś coś pokazuje. */
export function KsefUwagiBanery({ dane, onPokaz }) {
  if (!pokazUwagi(dane) && !pokazLogowanie(dane && dane.auth)) return null;
  return (
    <Odstep>
      <KsefUwagaBaner dane={dane} onPokaz={onPokaz} />
      <KsefLogowanieBaner auth={dane && dane.auth} />
    </Odstep>
  );
}

/** Strona główna: własne zapytanie, „Pokaż te faktury” prowadzi do przefiltrowanej listy. */
export function KsefUwagiStronaGlowna() {
  const navigate = useNavigate();
  const { dane } = useKsefUwagi();
  return <KsefUwagiBanery dane={dane} onPokaz={() => navigate('/invoices?ksef=attention')} />;
}
